import React, { useRef } from 'react';
import { FaClock, FaDollarSign, FaLock, FaTools, FaCloud, FaUserShield } from 'react-icons/fa';
import RoughBorder from '../../kits/LayoutKit/RoughBorder';
import { PaperBG } from '../../kits';
import MonoButton from '../../kits/DesignKit/MonoButton';

/**
 * Wiederverwendbare ZenPost Problem-Section Komponente
 * Zeigt die 3 Hauptprobleme mit Content-Erstellung
 */
const ZenPostProblemSection = ({
  title = "Das bekannte Content-Problem",
  subtitle = "Du schreibst einen Artikel. Dann copy-pastest du ihn auf LinkedIn. Dann formatierst du ihn für Medium. Dann kürzt du ihn für Twitter. Dann...",
  problems = null,
  showCTA = true,
  ctaText = "Es gibt einen besseren Weg",
  onCTAClick = null,
  usePaperBG = true,
  variant = "content", // "content" oder "writing"
  theme = "light"
}) => {
  const problemCard1Ref = useRef();
  const problemCard2Ref = useRef();
  const problemCard3Ref = useRef();

  // Default Problems für Content (Multi-Platform)
  const defaultContentProblems = [
    {
      icon: <FaClock className="text-orange-600" />,
   
      title: "Zeitverschwendung",
      description: "Stundenlang copy-paste zwischen Plattformen. Jede mit eigenem Format.",
      ref: problemCard1Ref
    },
    {
      icon: <FaDollarSign className="text-red-600" />,
    
      title: "Teure KI-APIs",
      description: "ChatGPT, Jasper, Copy.ai – alle kosten 30-50€/Monat. Für jedes Team-Mitglied.",
      ref: problemCard2Ref
    },
    {
      icon: <FaLock className="text-gray-600" />,
     
      title: "Keine Privatsphäre",
      description: "Deine Inhalte durch Cloud-KI-APIs. OpenAI liest mit. DSGVO-Risiko.",
      ref: problemCard3Ref
    }
  ];

  // Default Problems für Writing (Offline Editor)
  const defaultWritingProblems = [
    {
      icon: <FaTools className="text-orange-600" />,
      
      title: "Tool-Chaos",
      description: "Zu viele Editoren, zu viele Accounts. Schreiben wird zur Verwaltung.",
      ref: problemCard1Ref
    },
    {
      icon: <FaCloud className="text-blue-600" />,
     
      title: "Cloud-Abos",
      description: "Monatliche Kosten nur zum Schreiben – ohne echten Mehrwert.",
      ref: problemCard2Ref
    },
    {
      icon: <FaUserShield className="text-gray-600" />,
     
      title: "Wenig Kontrolle",
      description: "Deine Inhalte wandern in die Cloud. Datenschutz? Unklar.",
      ref: problemCard3Ref
    }
  ];

  const problemList = problems || (variant === "writing" ? defaultWritingProblems : defaultContentProblems);

  const isDark = theme === "dark";

  const content = (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8 sm:mb-12 px-4">
        <h2 className={isDark ? "text-2xl sm:text-3xl md:text-5xl font-extrabold text-white mb-3 sm:mb-4" : "text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4"}>
          {title}
        </h2>
        <p className={isDark ? "text-base sm:text-lg md:text-xl text-slate-300 max-w-3xl mx-auto" : "text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"}>
          {subtitle}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
        {problemList.map((problem, index) => (
          <div key={index} ref={problem.ref} className="relative h-full">
            <div className={isDark ? "bg-slate-950/60 border border-slate-700/60 p-4 sm:p-6 rounded-lg shadow-lg transition-all hover:shadow-xl h-full flex flex-col" : "bg-white p-4 sm:p-6 rounded-lg shadow-lg transition-all hover:shadow-xl h-full flex flex-col"}>
              {/* Icon und Emoji */}
              <div className="flex items-center gap-3 mb-2 sm:mb-3">
                <div className="text-3xl sm:text-4xl">{problem.emoji}</div>
                <div className="text-2xl sm:text-3xl">{problem.icon}</div>
              </div>

              {/* Title */}
              <h3 className={isDark ? "text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3" : "text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3"}>
                {problem.title}
              </h3>

              {/* Description */}
              <p className={isDark ? "text-sm sm:text-base text-slate-300 flex-grow" : "text-sm sm:text-base text-gray-600 flex-grow"}>
                {problem.description}
              </p>
            </div>
            <RoughBorder
              parentRef={problem.ref}
              radius={10}
              padding={2}
              color="#AC8E66"
              strokeWidth={0.3}
              roughness={1}
              bowing={2}
            />
          </div>
        ))}
      </div>

      {showCTA && (
        <div className="text-center">
          <MonoButton
            onClick={onCTAClick}
            className="text-sm sm:text-base md:text-xl px-6 py-3 sm:px-8 sm:py-4 border-brand"
            underlineColors="ZenBrand"
          >
            {ctaText}
          </MonoButton>
        </div>
      )}
    </div>
  );

  if (usePaperBG && !isDark) {
    return (
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-gray-50 via-brand/5 to-gray-50">
        <PaperBG>
          {content}
        </PaperBG>/var/folders/v2/mwt6p07j7hn_82b9qb33zsvh0000gn/T/TemporaryItems/NSIRD_screencaptureui_RDpgBE/Bildschirmfoto 2026-01-18 um 18.32.28.png
      </section>
    );
  }

  return (
    <section className={isDark ? "py-12 sm:py-16 px-4 bg-transparent" : "py-12 sm:py-16 px-4 bg-gray-50"}>
      {content}
    </section>
  );
};

export default ZenPostProblemSection;
